import React, { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCcw, MessageCircle } from "lucide-react";

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorId: string;
}

export class ChatErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    errorId: "",
  };

  public static getDerivedStateFromError(error: Error): State {
    const errorId = `chat-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log in development
    if (process.env.NODE_ENV === "development") {
      console.error("Chat widget error:", error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    // Example: errorReportingService.captureException(error, {
    //   tags: { component: 'ChatWidget' },
    //   extra: { errorInfo, errorId: this.state.errorId }
    // });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorId: "" });
    this.props.onReset?.();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 max-w-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Chat Error
                </h3>
                <p className="text-xs text-gray-500">Something went wrong</p>
              </div>
            </div>

            <p className="text-xs text-gray-600 mb-4">
              The chat widget encountered an error. Please try refreshing the
              chat.
            </p>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <div className="mb-4 p-2 bg-gray-50 rounded text-xs font-mono text-gray-700 overflow-auto max-h-20">
                {this.state.error.message}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={this.handleReset}
                className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors"
              >
                <RefreshCcw className="w-3 h-3" />
                Reset Chat
              </button>
            </div>

            {process.env.NODE_ENV === "development" && (
              <div className="mt-2 text-xs text-gray-400">
                Error ID: {this.state.errorId}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
